import compose from "ramda/src/compose";
import equals from "ramda/src/equals";
import type from "ramda/src/type";
import all from "ramda/src/all";
import values from "ramda/src/values";
import keysIn from "ramda/src/keysIn";
import concat from "ramda/src/concat";
import uniq from "ramda/src/uniq";

import { format } from "date-fns";

const isArray = compose(equals("Array"), type);
const isObject = compose(equals("Object"), type);
const isString = compose(equals("String"), type);
const allAreObjects = compose(all(isObject), values);

/**
 * Loops through an array and finds the object using the id property.
 */
const getArrayItemById = (id, records) => {
  for (const j in records) {
    if (records[j].id === id) {
      return records[j];
    }
  }
  return {};
};

/**
 * Obtains the difference between one GraphQL record and another.
 *
 * Here return just the differences (delta) of the record, taking into account that we must always send the record id to the server.
 * And if there were changes made to the record, the version property is also added.
 *
 * Notice that when dealing with arrays (to-many) we always return all of the objects in it. This is to handle the many-to-many relationships.
 */
// This can probably be written as a curried function with Ramda, at some point in time we may want to look at this: https://gist.github.com/sjzamora86/2df8d4cf4e32d36e2bb739ac7261f2c5
const recordDiff = (left = {}, right = {}) => {
  const keys = uniq(concat(keysIn(left), keysIn(right))),
    diff = {};

  for (const key of keys) {
    const targetValue = right[key];

    if (key === "id") {
      diff[key] = targetValue;
    }

    // Version always must be included in order to avoid the OptimisticLocking exception on the server
    if (key === "version") {
      diff[key] = targetValue;
    }

    if (isObject(targetValue)) {
      // To-one relationship, we get the delta values (if any) for that record
      diff[key] = recordDiff(left[key] || {}, targetValue);
    } else if (isArray(targetValue) && allAreObjects(targetValue)) {
      // To-many relationship, we loop through all of the objects and get the delta values of those but always return all of the records.
      const rightArray = targetValue,
        arr = [];

      for (const i in rightArray) {
        arr.push(recordDiff(getArrayItemById(rightArray[i].id, left[key]), rightArray[i]));
      }
      diff[key] = arr;
    } else if (!equals(left[key], targetValue)) {
      // Include the property if it is different than the original
      // Apollo does not like undefined values, it does not send those fields to the server. Final Form gives us undefined when the field is cleared (null).
      diff[key] = targetValue === undefined ? null : targetValue;
    } else if (equals(left[key], targetValue) && right["version"] === undefined) {
      // Include the property if the record (and the property) is new.
      // We know that a record is new when version is undefined
      diff[key] = targetValue
    }
  }

  // We have to ensure that we are comparing the same record, because SelectFields can select to-many records and the version for the previous record would be used.
  if (left.id === diff.id && left["version"] && right["version"] !== undefined) {
    // Since we are always returning all of the records in an array, we only include the version if a property changed. This solves the many-to-many problem from being merged on the server.
    diff["version"] = left["version"];
  } else if (right["version"] === undefined) {
    // There are some records created on ClientSide, for that reason we might get its undefined value to persist them on ServerSide correctly.
    // Change made to set Delta correctly on Operator App.
    diff["version"] = right["version"];
  }

  return diff;
};

/**
 * Encodes an object into a HTTP query, taking into account date objects.
 *
 * This means that it takes a JavaScript object like this:
 * {
 *   test: "value1",
 *   date1: new Date()
 * }
 *
 * and turns this into:
 *
 * test=value&date1=2018-08-16
 */
const encodeObjectToUrl = objectToEncode => Object.entries(objectToEncode).map(
  ([key, val]) => {
    if (!val) {
      // We don't encode null or undefined values
      return;
    }

    let formattedValue = val;

    if (val instanceof Date) {
      formattedValue = format(val, "yyyy-MM-dd");
    }

    return `${key}=${encodeURIComponent(formattedValue)}`;
  }
).join('&');

/**
 * Returns an array with elements contained in array 1 which are not in array 2.
 *
 * Parameters:
 * - equalsFn: Function used to determine when 2 items are equals.
 * - arr1: Array 1.
 * - arr2: Array 2.
 *
 * Example:
 *     equalsFn ------> (item1, item2) => item1.id === item2.id
 *     arr1 ----------> [{ id: 1, name: "Jose" }, { id: 2, name: "Hector" }]
 *     arr2 ----------> [{ id: 2, name: "Hector" }]
 *
 *     Result --------> [{ id: 1, name: "Jose" }]
 */
const arrayDifference = (equalsFn, arr1, arr2) => {
  const result = [];
  arr1.forEach((item1, index) => {
    if (!arr2.find(item2 => equalsFn(item1, item2))) {
      result.push(item1);
    }
  });
  return result;
};

/*
 * Function that receives a Date object and returns an string in the Java Instant ISO 8601 format such as 2011-12-03T10:15:30Z.
 */
const dateToInstantISOString = date => date.toISOString().replace(/\.[0-9]{2,3}/, '');

/**
 * Regular Expression to evaluate CCI Server Date Format: yyyy-mm-dd
 */
const serverFormatDateRegex = /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/;

/**
 * Regular Expression to evaluate CCI Server Time Format: HH:MM
 */
const serverFormatTimeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

export {
  isArray,
  isObject,
  isString,
  allAreObjects,
  recordDiff,
  encodeObjectToUrl,
  arrayDifference,
  dateToInstantISOString,
  serverFormatDateRegex,
  serverFormatTimeRegex,
};
